import {FC, useEffect} from "react";
import {useQuery} from "@tanstack/react-query";
import {IAccountant, IUser} from "api/types";
import {useParams, useNavigate, Navigate} from "react-router";
import LoadedAccountantProfilePage from "./Loaded";
import shareApi from "api/share";
import useMaybeNotSetupUser from "contexts/MaybeNotSetupUser/useMaybeNotSetupUser";

const canSeeAccountants = (type: IUser["type"]) => type === "admin" || type === "recruiter";

const SharedAccountantProfilePage: FC = () => {
  const navigate = useNavigate();
  const authUser = useMaybeNotSetupUser();
  const shareToken = useParams().shareToken;

  const {data: user, isSuccess} = useQuery<IAccountant | null>({
    queryKey: ["shared-profile", shareToken],
    queryFn: () => shareApi.getSharedAccountantProfile(shareToken!),
    enabled: !!shareToken,
  });

  useEffect(() => {
    if (!shareToken) navigate("/", {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  useEffect(() => {
    if (isSuccess && !user) navigate("/", {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, user]);

  if (authUser.user?.id && user?.id) {
    if (authUser.user.id === user.id) return <Navigate to="/profile" replace />;

    if (canSeeAccountants(authUser.user.type)) {
      return <Navigate to={`/accountant/${user.id}`} replace />;
    }
  }

  if (user) return <LoadedAccountantProfilePage user={user} shareToken={shareToken} />;

  return (
    <div className="w-full h-full overflow-hidden p-5 flex flex-col">
      <div className="flex items-center pb-5 gap-2">
        <span className="font-semibold text-2xl text-brand-navy">Accountant Profile</span>
      </div>
      <div className="grow w-full overflow-y-auto border-t border-brand-border pt-4 relative">
        <div className="flex grow w-full gap-4 overflow-x-hidden">
          <div className="rounded-xl bg-brand-cream-dark p-3 w-[16.875rem] gap-2.5 h-[30rem] sticky top-0 animate-pulse" />
          <div className="grid grid-cols-2 2xl:grid-cols-4 gap-4 grow h-max pb-10">
            <div className="h-[10rem] rounded-xl bg-brand-cream-dark animate-pulse" />
            <div className="h-[10rem] rounded-xl bg-brand-cream-dark animate-pulse" />
            <div className="h-[10rem] rounded-xl bg-brand-cream-dark animate-pulse" />
            <div className="h-[10rem] rounded-xl bg-brand-cream-dark animate-pulse" />
            <div className="h-[24rem] rounded-xl bg-brand-cream-dark animate-pulse col-span-2" />
            <div className="h-[24rem] rounded-xl bg-brand-cream-dark animate-pulse col-span-2" />
            <div className="h-[24rem] rounded-xl bg-brand-cream-dark animate-pulse col-span-2 2xl:col-span-4" />
            <div className="h-[24rem] rounded-xl bg-brand-cream-dark animate-pulse col-span-2" />
            <div className="h-[24rem] rounded-xl bg-brand-cream-dark animate-pulse col-span-2" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedAccountantProfilePage;
